import { Guid } from "../../DiscoveryEngine.TS/Utils/Guid";
import { List } from "../../DiscoveryEngine.TS/Utils/List";
import { UserLink } from "../../DiscoveryEngine.TS/Discovery/UserLink";

import { ValueEncoder } from "../../DiscoveryEngine.TS/Utils/ValueEncoder";
import { ProtocolSettings } from "../../DiscoveryEngine.TS/Protocols/ProtocolSettings";
import { DiscoverySettings } from "../../DiscoveryEngine.TS/Discovery/DiscoverySettings";
import { ScheduledDiscovery } from "../../DiscoveryEngine.TS/Discovery/ScheduledDiscovery";
import { SmtpSettings } from "../../DiscoveryEngine.TS/Smtp/SmtpSettings";
import { DrawSettings } from "../../DiscoveryEngine.TS/Topology/DrawSettings";
import { ViewerDeviceFilterDto } from "../Viewer/ViewerDeviceFilterDto";
import { ProtocolSettingsFactory } from "../../DiscoveryEngine.TS/Protocols/ProtocolSettingsFactory";
import { ObjSerUtils } from "../../DiscoveryEngine.TS/Serialization/ObjSerUtils";
import { StorageRootDto } from "../StorageRootDto";
import { UpdateResultDto } from "../UpdateResultDto";
import { SchedDiscPrtgExport } from "../../DiscoveryEngine.TS/PRTG/SchedDiscPrtgExport";
import { SlackSettings } from "UvExplorerServer.Dtos.TS/Tools";


export class NetViewTreeNodeDto extends StorageRootDto {

	public UserGuid: string;
	public ScopeGuid: string;
	public Name: string;
	public Description: string;
	public ProtocolSettings: List<ProtocolSettings>;
	public DiscoverySettings: List<DiscoverySettings>;
	public ScheduledDiscoveries: List<ScheduledDiscovery>;
	public DeviceFilters: List<ViewerDeviceFilterDto>;
	public SmtpSettings: SmtpSettings;
	public SlackSettings: SlackSettings;
	public MapDrawSettings: DrawSettings;
	public UserLinks: List<UserLink>;
	public RollupPrtgExport: SchedDiscPrtgExport;
	public DiscoveryStatusInterval: number;
	public EventsInterval: number;
	public AgentAutoUpdate: boolean;
	public AgentAutoUpdateBeginTime: Date;
	public AgentAutoUpdateEndTime: Date;
	public AgentInstallerLocation: string;
	public Owner: string;


	//TODO
	//Monitor definitions

	// Not serialized
	public HasChanges: boolean;

	constructor() {
		super();

		this.UserGuid = "";
		this.ScopeGuid = "";
		this.Name = "";
		this.Description = "";
		this.ProtocolSettings = new List<ProtocolSettings>();
		this.DiscoverySettings = new List<DiscoverySettings>();
		this.ScheduledDiscoveries = new List<ScheduledDiscovery>();
        this.UserLinks = new List<UserLink>();
		this.DeviceFilters = new List<ViewerDeviceFilterDto>();
		this.SmtpSettings = null;
		this.SlackSettings = null;
		this.MapDrawSettings = null;
		this.RollupPrtgExport = new SchedDiscPrtgExport();
		this.DiscoveryStatusInterval = 60;
		this.EventsInterval = 120;
		this.AgentAutoUpdate = true;
		this.AgentAutoUpdateBeginTime = this.defaultBeginTime;
		this.AgentAutoUpdateEndTime = this.defaultEndTime;
		this.AgentInstallerLocation = "";
		this.Owner = "";

		// Not serialized
		this.HasChanges = false;
	}

	private get defaultBeginTime(): Date {
		let now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 2, 0, 0, 0);
	}

	private get defaultEndTime(): Date {
		let now = new Date();
		return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 4, 0, 0, 0);
	}

	public get HasProtocolSettings(): boolean {
		return (this.ProtocolSettings && this.ProtocolSettings.Count > 0);
	}

	public get HasDiscoverySettings(): boolean {
		return (this.DiscoverySettings && this.DiscoverySettings.Count > 0);
	}

	public get HasScheduledDiscoveries(): boolean {
		return (this.ScheduledDiscoveries && this.ScheduledDiscoveries.Count > 0);
	}

	public get HasDeviceFilters(): boolean {
		return (this.DeviceFilters && this.DeviceFilters.Count > 0);
	}

    public get HasUserLinks(): boolean {
        return (this.UserLinks && this.UserLinks.Count > 0);
    }

	public get HasSmtpSettings(): boolean {
		return !!this.SmtpSettings;
	}

	public get HasSlackSettings(): boolean {
		return !!this.SlackSettings;
	}

	public get HasMapDrawSettings(): boolean {
		return !!this.MapDrawSettings;
	}

	public get HasRollupPrtgExport(): boolean {
		return !!this.RollupPrtgExport;
	}

	public get HasDiscoveryStatusInterval(): boolean {
		return (this.DiscoveryStatusInterval > 0);
	}

	public get HasEventsInterval(): boolean {
		return (this.EventsInterval > 0);
	}

	public get HasAgentInstallerLocation(): boolean {
		return !!this.AgentInstallerLocation;
	}

	public get HasOwner(): boolean {
		return !!this.Owner;
	}

	public VerifyGuid(): string {
		if (!this.Guid) {
			this.Guid = Guid.Create().toString().toLowerCase();
		}
		return this.Guid;
	}

	public CopyProps(obj: UpdateResultDto, isCreate: boolean = true) {
		super.CopyProps(obj, isCreate);

		if (isCreate) {
			this.UserGuid = obj.UserGuid;
			this.ScopeGuid = obj.ScopeGuid;
			this.ServerGuid = obj.ServerGuid;
			this.TenantGuid = obj.TenantGuid;
		}
	}

	public GetScheduledDiscovery(guid: string): ScheduledDiscovery {
		let result: ScheduledDiscovery = null;

		this.ScheduledDiscoveries.Some((value: ScheduledDiscovery) => {
			if (value.GUID == guid) {
				result = value;
				return true;
			}
			else {
				return false;
			}
		});

		return result;
	}

    public DeleteUserLink(GUID: string) {
        this.UserLinks.Values = this.UserLinks.Values.filter(function (obj) {
            return obj.GUID !== GUID;
        });
    }

	public Write(obj: any, context: any): void {

		// Called by subclass Write methods

		super.Write(obj, context);

		if (this.UserGuid)
			obj.user_guid = this.UserGuid;

		if (this.ScopeGuid)
			obj.scope_guid = this.ScopeGuid;

		if (this.Name)
			obj.name = this.Name;

		if (this.Description)
			obj.description = this.Description;

		if (this.HasProtocolSettings) {
			obj.protocol_settings = [];
			ObjSerUtils.WriteObjSerCollection(obj.protocol_settings, this.ProtocolSettings, context);
		}

		if (this.HasDiscoverySettings) {
			obj.discovery_settings = [];
			ObjSerUtils.WriteObjSerCollection(obj.discovery_settings, this.DiscoverySettings, context);
		}

		if (this.HasScheduledDiscoveries) {
			obj.scheduled_discoveries = [];
			ObjSerUtils.WriteObjSerCollection(obj.scheduled_discoveries, this.ScheduledDiscoveries, context);
		}

		if (this.HasDeviceFilters) {
			obj.device_filters = [];
			ObjSerUtils.WriteObjSerCollection(obj.device_filters, this.DeviceFilters, context);
		}

		if (this.HasSmtpSettings) {
			this.SmtpSettings.Write(obj, context);
		}

		if (this.HasSlackSettings) {
			this.SlackSettings.Write(obj, context);
		}

		if (this.HasMapDrawSettings) {
			this.MapDrawSettings.Write(obj, context);
		}

		if (this.HasRollupPrtgExport) {
			this.RollupPrtgExport.Write(obj, context);
		}

		obj.discovery_status_interval = ValueEncoder.EncodeInteger(this.DiscoveryStatusInterval);
		obj.events_interval = ValueEncoder.EncodeInteger(this.EventsInterval);

        if (this.HasUserLinks) {
            obj.user_links = [];
            ObjSerUtils.WriteObjSerCollection(obj.user_links, this.UserLinks, context);
        }

		obj.agent_auto_update = ValueEncoder.EncodeBoolean(this.AgentAutoUpdate);
		obj.agent_auto_update_begin_time = ValueEncoder.EncodeDateTime(this.AgentAutoUpdateBeginTime);
		obj.agent_auto_update_end_time = ValueEncoder.EncodeDateTime(this.AgentAutoUpdateEndTime);

		if (this.HasAgentInstallerLocation) {
			obj.agent_installer_location = this.AgentInstallerLocation;
		}

		if (this.HasOwner) {
			obj.owner = this.Owner;
		}
	}

	public Read(obj: any, context: any): void {

		// Called by subclass Read methods

		super.Read(obj, context);

		if (obj.user_guid !== undefined)
			this.UserGuid = obj.user_guid;

		if (obj.scope_guid !== undefined)
			this.ScopeGuid = obj.scope_guid;

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.description !== undefined)
			this.Description = obj.description;

		if (obj.protocol_settings !== undefined) {
			ObjSerUtils.ReadObjSerCollection(obj.protocol_settings, this.ProtocolSettings,
				(value: any) => ProtocolSettingsFactory.Create(value), context);
		}

		if (obj.discovery_settings !== undefined) {
			ObjSerUtils.ReadObjSerCollection(obj.discovery_settings, this.DiscoverySettings,
				(value: any) => new DiscoverySettings(), context);
		}

		if (obj.scheduled_discoveries !== undefined) {
			ObjSerUtils.ReadObjSerCollection(obj.scheduled_discoveries, this.ScheduledDiscoveries,
				(value: any) => new ScheduledDiscovery(), context);
		}

		if (obj.device_filters !== undefined) {
			ObjSerUtils.ReadObjSerCollection(obj.device_filters, this.DeviceFilters,
				(value: any) => new ViewerDeviceFilterDto(), context);
		}

		if (obj.smtp_settings !== undefined) {
			this.SmtpSettings = new SmtpSettings();
			this.SmtpSettings.Read(obj, context);
		}

		if (obj.slack_settings !== undefined) {
			this.SlackSettings = new SlackSettings();
			this.SlackSettings.Read(obj, context);
		}

		if (obj.draw_settings !== undefined) {
			this.MapDrawSettings = new DrawSettings();
			this.MapDrawSettings.Read(obj, context);
		}

		if (obj.sched_disc_prtg_export !== undefined) {
			this.RollupPrtgExport = new SchedDiscPrtgExport();
			this.RollupPrtgExport.Read(obj, context);
		}

		if (obj.discovery_status_interval !== undefined) {
			this.DiscoveryStatusInterval = ValueEncoder.DecodeInteger(obj.discovery_status_interval);
		}

		if (obj.events_interval !== undefined) {
			this.EventsInterval = ValueEncoder.DecodeInteger(obj.events_interval);
        }

        if (obj.user_links !== undefined) {
            ObjSerUtils.ReadObjSerCollection(obj.user_links, this.UserLinks,
                (value: any) => new UserLink(), context);
		}

		if (obj.agent_auto_update !== undefined) {
			this.AgentAutoUpdate = ValueEncoder.DecodeBoolean(obj.agent_auto_update);
		}

		if (obj.agent_auto_update_begin_time !== undefined) {
			this.AgentAutoUpdateBeginTime = ValueEncoder.DecodeDateTime(obj.agent_auto_update_begin_time);
		}

		if (obj.agent_auto_update_end_time !== undefined) {
			this.AgentAutoUpdateEndTime = ValueEncoder.DecodeDateTime(obj.agent_auto_update_end_time);
		}

		if (obj.agent_installer_location !== undefined) {
			this.AgentInstallerLocation = obj.agent_installer_location;
		}

		if (obj.owner !== undefined) {
			this.Owner = obj.owner;
		}
	}

}
